/**
 * ガチャ(docs/20 §5・docs/19 §3 GachaDef)。
 * 一覧: 科目 / 価格 / 件数 / 期間 / 状態。
 * 編集: 価格(cost / cost10)、排出率(合計1)・保証・重複変換、プール(条件で一括追加・除外・weight 一括設定)、
 * 期間、状態。模擬抽選: 1,000回をブラウザ内で回してレアリティ別・アイテム別の結果表を出す(保存しない)。
 */
import { useMemo, useState } from 'react';
import {
  GACHA_STATUSES,
  RARITIES,
  type CharacterDef,
  type GachaDef,
  type ItemDef,
  type Rarity,
} from '@paltyping/content-schema';
import {
  addPoolEntries,
  fieldIssues,
  issuesFor,
  poolCandidates,
  rateSum,
  removePoolEntries,
  setPoolWeights,
  validateDrafts,
  duplicateGachaDef,
  newGachaDef,
  newIdProblem,
  nextOrder,
  type CatalogDrafts,
  type PoolFilter,
} from '../../services/catalog';
import {
  GachaSimError,
  jstMonth,
  seededRng,
  simulateGacha,
  type SimMode,
  type SimResult,
} from '../../services/gachaSim';
import {
  CatalogSummary,
  IssueList,
  NumberInput,
  ReadOnlyNote,
  SaveNotice,
  type CatalogStore,
  type SaveOutcome,
} from './common';

export const GACHA_STATUS_LABELS: Readonly<Record<GachaDef['status'], string>> = {
  active: '有効',
  paused: '休止',
  retired: '廃止',
};

export const SIM_DRAWS = 1000;

interface Props {
  readonly store: CatalogStore;
  readonly editable: boolean;
  /** 模擬抽選の乱数(テストでは固定)。省略は毎回ちがう種。 */
  readonly rngSeed?: () => number;
}

function periodText(period: GachaDef['period']): string {
  if (period.from === null && period.to === null) return '無期限';
  return `${period.from ?? '—'} 〜 ${period.to ?? '—'}`;
}

export function GachasPanel({ store, editable, rngSeed }: Props) {
  const { drafts } = store;
  const [selectedId, setSelectedId] = useState<string | null>(drafts.gacha[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const summary = useMemo(() => validateDrafts(drafts), [drafts]);
  const subjectNames = new Map(drafts.subject.map((entry) => [entry.id, entry.data.def.name.ja]));
  const selected = drafts.gacha.find((entry) => entry.id === selectedId);
  const [creating, setCreating] = useState<GachaDef | null>(null);

  async function save(def: GachaDef): Promise<void> {
    setBusy(true);
    setOutcome(null);
    const existing = drafts.gacha.find((entry) => entry.id === def.id);
    // 新規は expectedRevision 0(buildSaveOps が下書きに無いものを 0 にする)。
    const order = existing ? existing.data.order : nextOrder(drafts.gacha);
    const result = await store.save([{ docType: 'gacha', id: def.id, data: { order, def } }]);
    setOutcome(result);
    if (result.ok && creating?.id === def.id) {
      setCreating(null);
      setSelectedId(def.id);
    }
    setBusy(false);
  }

  return (
    <div className="catalog-split">
      <div className="pane pane--list">
        <div className="toolbar">
          <h2 className="section-title">ガチャ</h2>
          <CatalogSummary errors={summary.errors.length} warnings={summary.warnings.length} />
        </div>
        <table className="table" aria-label="ガチャ一覧">
          <thead>
            <tr>
              <th>ID / 名前</th>
              <th>科目</th>
              <th>価格</th>
              <th>件数</th>
              <th>期間</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            {drafts.gacha.map((entry) => {
              const gacha = entry.data.def;
              return (
                <tr
                  key={entry.id}
                  className={entry.id === selectedId && !creating ? 'row--active' : undefined}
                  onClick={() => {
                    setOutcome(null);
                    setCreating(null);
                    setSelectedId(entry.id);
                  }}
                >
                  <td>
                    <span className="mono">{entry.id}</span>
                    <div className="muted">{gacha.name.ja}</div>
                  </td>
                  <td>
                    <span className="mono">{gacha.subjectId}</span>
                    <div className="muted">{subjectNames.get(gacha.subjectId) ?? ''}</div>
                  </td>
                  <td className="nowrap">
                    {gacha.cost} / {gacha.cost10}
                  </td>
                  <td>{gacha.pool.length}</td>
                  <td>{periodText(gacha.period)}</td>
                  <td>{GACHA_STATUS_LABELS[gacha.status] ?? gacha.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {editable && (
          <NewGachaForm
            drafts={drafts}
            onCreate={(def) => {
              setOutcome(null);
              setCreating(def);
            }}
          />
        )}
      </div>
      <div className="pane">
        <SaveNotice outcome={outcome} onReload={() => void store.reload()} />
        {creating ? (
          <GachaEditor
            key={`new:${creating.id}`}
            initial={creating}
            isNew
            store={store}
            editable={editable}
            busy={busy}
            rngSeed={rngSeed}
            onSave={save}
            onCancel={() => {
              setCreating(null);
            }}
          />
        ) : selected ? (
          <GachaEditor
            key={`${selected.id}:${selected.revision}`}
            initial={selected.data.def}
            store={store}
            editable={editable}
            busy={busy}
            rngSeed={rngSeed}
            onSave={save}
          />
        ) : (
          <p className="muted">左の一覧からガチャを選んでください。</p>
        )}
      </div>
    </div>
  );
}

type CreateMode = 'subject' | 'copy';

/** ガチャの追加: 科目から新しく作る / 既存のガチャを複製する。 */
function NewGachaForm({
  drafts,
  onCreate,
}: {
  readonly drafts: CatalogDrafts;
  readonly onCreate: (def: GachaDef) => void;
}) {
  const [mode, setMode] = useState<CreateMode>('subject');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [subjectId, setSubjectId] = useState(drafts.subject[0]?.id ?? '');
  const [sourceId, setSourceId] = useState(drafts.gacha[0]?.id ?? '');
  const trimmedId = id.trim();
  const problem = newIdProblem(trimmedId, drafts.gacha);
  const subject = drafts.subject.find((entry) => entry.id === subjectId)?.data.def;
  const source = drafts.gacha.find((entry) => entry.id === sourceId)?.data.def;
  const ready =
    problem === null && (mode === 'subject' ? subject !== undefined : source !== undefined);

  return (
    <section className="card" aria-label="ガチャを追加">
      <h3>ガチャを追加</h3>
      <div className="toolbar toolbar--wrap">
        <select
          className="select select--inline"
          aria-label="作り方"
          value={mode}
          onChange={(event) => {
            setMode(event.target.value as CreateMode);
          }}
        >
          <option value="subject">科目から新しく作る</option>
          <option value="copy" disabled={drafts.gacha.length === 0}>
            既存のガチャを複製する
          </option>
        </select>
        {mode === 'subject' ? (
          <select
            className="select select--inline"
            aria-label="新しいガチャの科目"
            value={subjectId}
            onChange={(event) => {
              setSubjectId(event.target.value);
            }}
          >
            {drafts.subject.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.id}(通貨 {entry.data.def.currency} / キャラ {entry.data.def.characterId})
              </option>
            ))}
          </select>
        ) : (
          <select
            className="select select--inline"
            aria-label="複製元のガチャ"
            value={sourceId}
            onChange={(event) => {
              setSourceId(event.target.value);
            }}
          >
            {drafts.gacha.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.id}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="toolbar toolbar--wrap">
        <input
          className="input mono"
          aria-label="新しいガチャのID"
          placeholder="ID(例 math-hero-02)"
          value={id}
          onChange={(event) => {
            setId(event.target.value);
          }}
        />
        <input
          className="input"
          aria-label="新しいガチャの名前"
          placeholder="名前(ja)"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <button
          className="button button--small"
          type="button"
          disabled={!ready}
          onClick={() => {
            if (mode === 'subject' && subject) {
              onCreate(newGachaDef(trimmedId, name.trim(), subject));
            } else if (source) {
              onCreate(duplicateGachaDef(source, trimmedId, name.trim()));
            }
            setId('');
            setName('');
          }}
        >
          作成
        </button>
      </div>
      {trimmedId !== '' && problem !== null && (
        <p className="field-hint field-hint--error" role="alert">
          {problem}
        </p>
      )}
      <p className="muted">
        休止(paused)で作ります。保存するまで下書きにはなりません。通貨・キャラクターは科目のものになり、作成後は変えられません。
      </p>
    </section>
  );
}

interface EditorProps {
  readonly initial: GachaDef;
  readonly isNew?: boolean;
  readonly onCancel?: () => void;
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly busy: boolean;
  readonly rngSeed: (() => number) | undefined;
  readonly onSave: (def: GachaDef) => Promise<void>;
}

const POOL_PAGE = 100;

function GachaEditor({
  initial,
  isNew = false,
  onCancel,
  store,
  editable,
  busy,
  rngSeed,
  onSave,
}: EditorProps) {
  const [working, setWorking] = useState<GachaDef>(initial);
  const [poolFilter, setPoolFilter] = useState<PoolFilter>({ slot: '', rarity: '' });
  const [bulkWeight, setBulkWeight] = useState(1);
  const [showPool, setShowPool] = useState(false);
  const [simMode, setSimMode] = useState<SimMode>('single');
  const [sim, setSim] = useState<SimResult | null>(null);
  const [simError, setSimError] = useState<string | null>(null);

  const { drafts } = store;
  const characters: readonly CharacterDef[] = useMemo(
    () => drafts.character.map((entry) => entry.data.def),
    [drafts.character],
  );
  const items: readonly ItemDef[] = useMemo(
    () => drafts.item.map((entry) => entry.data.def),
    [drafts.item],
  );
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const result = useMemo(() => validateDrafts(drafts, { gacha: working }), [drafts, working]);
  const issues = issuesFor(result, 'gacha', working.id);
  const character = characters.find((entry) => entry.id === working.characterId);
  const candidates = poolCandidates(working, characters, items, poolFilter);
  const matchingPool = working.pool.filter((entry) => {
    const item = itemsById.get(entry.itemId);
    if (!item) return poolFilter.slot === '' && poolFilter.rarity === '';
    return (
      (poolFilter.slot === '' || item.slot === poolFilter.slot) &&
      (poolFilter.rarity === '' || item.rarity === poolFilter.rarity)
    );
  });
  const sum = rateSum(working.rarityRates);
  const poolByRarity = RARITIES.map((rarity) => ({
    rarity,
    count: working.pool.filter((entry) => itemsById.get(entry.itemId)?.rarity === rarity).length,
  }));

  function update(patch: Partial<GachaDef>): void {
    setWorking((current) => ({ ...current, ...patch }));
  }

  function setRate(rarity: Rarity, value: number): void {
    update({ rarityRates: { ...working.rarityRates, [rarity]: value } });
  }

  function setConvert(rarity: Rarity, value: number): void {
    update({ duplicateConvertCoins: { ...working.duplicateConvertCoins, [rarity]: value } });
  }

  function setWeight(itemId: string, weight: number): void {
    update({
      pool: working.pool.map((entry) => (entry.itemId === itemId ? { ...entry, weight } : entry)),
    });
  }

  function runSim(): void {
    setSimError(null);
    try {
      const seed = rngSeed ? rngSeed() : Math.floor(Math.random() * 2 ** 32);
      setSim(
        simulateGacha(
          working,
          itemsById,
          { draws: SIM_DRAWS, mode: simMode, month: jstMonth(new Date()) },
          seededRng(seed),
        ),
      );
    } catch (cause) {
      setSim(null);
      setSimError(cause instanceof GachaSimError ? cause.message : String(cause));
    }
  }

  return (
    <form
      className="editor"
      aria-label="ガチャの編集"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(working);
      }}
    >
      <h3>
        {isNew ? '新しいガチャ' : 'ガチャ'} <span className="mono">{working.id}</span>
      </h3>
      <ReadOnlyNote editable={editable} />
      <IssueList issues={issues} />
      <div className="meta-grid">
        <span className="meta-grid__key">科目</span>
        <span className="mono">{working.subjectId}</span>
        <span className="meta-grid__key">キャラクター</span>
        <span className="mono">{working.characterId}</span>
        <span className="meta-grid__key">通貨</span>
        <span className="mono">{working.currency}</span>
      </div>
      <fieldset className="editor__fields" disabled={!editable || busy}>
        <label className="field">
          <span className="field__label">名前(ja)</span>
          <input
            className="input"
            aria-label="名前"
            value={working.name.ja ?? ''}
            onChange={(event) => {
              update({ name: { ...working.name, ja: event.target.value } });
            }}
          />
          <IssueList issues={fieldIssues(issues, 'name')} />
        </label>
        <div className="field">
          <span className="field__label">価格(単発 / 連)</span>
          <div className="toolbar">
            <NumberInput
              label="単発の価格"
              value={working.cost}
              onChange={(cost) => {
                update({ cost });
              }}
            />
            <NumberInput
              label="連の価格"
              value={working.cost10}
              onChange={(cost10) => {
                update({ cost10 });
              }}
            />
          </div>
          <IssueList issues={fieldIssues(issues, 'cost')} />
        </div>
        <div className="field">
          <span className="field__label">排出率(合計 1)</span>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>レア</th>
                <th>排出率</th>
                <th>重複変換コイン</th>
                <th>プール件数</th>
              </tr>
            </thead>
            <tbody>
              {RARITIES.map((rarity) => (
                <tr key={rarity}>
                  <td>{rarity}</td>
                  <td>
                    <NumberInput
                      label={`排出率 ${rarity}`}
                      value={working.rarityRates[rarity]}
                      onChange={(value) => {
                        setRate(rarity, value);
                      }}
                    />
                  </td>
                  <td>
                    <NumberInput
                      label={`重複変換 ${rarity}`}
                      value={working.duplicateConvertCoins[rarity]}
                      onChange={(value) => {
                        setConvert(rarity, value);
                      }}
                    />
                  </td>
                  <td>{poolByRarity.find((entry) => entry.rarity === rarity)?.count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <span
            className={Math.abs(sum - 1) > 1e-9 ? 'summary summary--error' : 'summary'}
            data-testid="rate-sum"
          >
            合計 {Number(sum.toFixed(6))}
          </span>
          <IssueList issues={fieldIssues(issues, 'rarityRates')} />
          <IssueList issues={fieldIssues(issues, 'duplicateConvertCoins')} />
        </div>
        <div className="field">
          <span className="field__label">
            保証(この連数で、このレア以上が1つも出なければ最後を引き直す)
          </span>
          <div className="toolbar">
            <NumberInput
              label="保証の連数"
              value={working.guarantee.count}
              onChange={(count) => {
                update({ guarantee: { ...working.guarantee, count } });
              }}
            />
            <select
              className="select select--inline"
              aria-label="保証のレア"
              value={working.guarantee.rarity}
              onChange={(event) => {
                update({
                  guarantee: { ...working.guarantee, rarity: event.target.value as Rarity },
                });
              }}
            >
              {RARITIES.map((rarity) => (
                <option key={rarity} value={rarity}>
                  {rarity} 以上
                </option>
              ))}
            </select>
          </div>
          <IssueList issues={fieldIssues(issues, 'guarantee')} />
        </div>
        <div className="field">
          <span className="field__label">期間(ISO8601。空欄は無期限)</span>
          <div className="toolbar">
            <input
              className="input mono"
              aria-label="開始"
              placeholder="開始(例 2026-11-01T00:00:00+09:00)"
              value={working.period.from ?? ''}
              onChange={(event) => {
                const from = event.target.value.trim();
                update({ period: { ...working.period, from: from === '' ? null : from } });
              }}
            />
            <input
              className="input mono"
              aria-label="終了"
              placeholder="終了"
              value={working.period.to ?? ''}
              onChange={(event) => {
                const to = event.target.value.trim();
                update({ period: { ...working.period, to: to === '' ? null : to } });
              }}
            />
          </div>
          <IssueList issues={fieldIssues(issues, 'period')} />
        </div>
        <label className="field">
          <span className="field__label">状態</span>
          <select
            className="select"
            aria-label="状態"
            value={working.status}
            onChange={(event) => {
              update({ status: event.target.value as GachaDef['status'] });
            }}
          >
            {GACHA_STATUSES.map((status) => (
              <option key={status} value={status}>
                {GACHA_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        <section className="card" aria-label="プール">
          <h3>
            プール({working.pool.length} 件。{character?.id ?? working.characterId}{' '}
            の装備と、starter でない素体だけ)
          </h3>
          <div className="toolbar toolbar--wrap">
            <select
              className="select select--inline"
              aria-label="プール条件の枠"
              value={poolFilter.slot}
              onChange={(event) => {
                setPoolFilter({ ...poolFilter, slot: event.target.value });
              }}
            >
              <option value="">枠: すべて</option>
              {(character?.slots ?? []).map((slot) => (
                <option key={slot.id} value={slot.id}>
                  {slot.id}({slot.name.ja}
                  {slot.kind === 'base' ? '・素体' : ''})
                </option>
              ))}
            </select>
            <select
              className="select select--inline"
              aria-label="プール条件のレア"
              value={poolFilter.rarity}
              onChange={(event) => {
                setPoolFilter({
                  ...poolFilter,
                  rarity: event.target.value as PoolFilter['rarity'],
                });
              }}
            >
              <option value="">レア: すべて</option>
              {RARITIES.map((rarity) => (
                <option key={rarity} value={rarity}>
                  {rarity}
                </option>
              ))}
            </select>
            <span className="muted">weight</span>
            <NumberInput label="一括の weight" value={bulkWeight} onChange={setBulkWeight} />
          </div>
          <div className="toolbar toolbar--wrap">
            <button
              className="button button--small"
              type="button"
              disabled={candidates.length === 0 || !(bulkWeight > 0)}
              onClick={() => {
                setWorking(addPoolEntries(working, characters, items, poolFilter, bulkWeight));
              }}
            >
              条件に合う {candidates.length} 件を追加
            </button>
            <button
              className="button button--small"
              type="button"
              disabled={matchingPool.length === 0}
              onClick={() => {
                setWorking(removePoolEntries(working, itemsById, poolFilter));
              }}
            >
              条件に合う {matchingPool.length} 件を除外
            </button>
            <button
              className="button button--small"
              type="button"
              disabled={matchingPool.length === 0 || !(bulkWeight > 0)}
              onClick={() => {
                setWorking(setPoolWeights(working, itemsById, poolFilter, bulkWeight));
              }}
            >
              条件に合う {matchingPool.length} 件の weight を設定
            </button>
          </div>
          <IssueList issues={fieldIssues(issues, 'pool')} />
          <button
            className="button button--small"
            type="button"
            onClick={() => {
              setShowPool((value) => !value);
            }}
          >
            {showPool ? 'プールの中身を閉じる' : 'プールの中身を見る'}
          </button>
          {showPool && (
            <table className="table table--compact" aria-label="プールの中身">
              <thead>
                <tr>
                  <th>アイテム</th>
                  <th>枠</th>
                  <th>レア</th>
                  <th>weight</th>
                  <th>月限定</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {matchingPool.slice(0, POOL_PAGE).map((entry) => {
                  const item = itemsById.get(entry.itemId);
                  return (
                    <tr key={entry.itemId}>
                      <td>
                        <span className="mono">{entry.itemId}</span>
                        <div className="muted">{item?.name.ja ?? '(カタログに無い)'}</div>
                      </td>
                      <td className="mono">{item?.slot ?? '—'}</td>
                      <td>{item?.rarity ?? '—'}</td>
                      <td>
                        <NumberInput
                          label={`${entry.itemId} の weight`}
                          value={entry.weight}
                          onChange={(weight) => {
                            setWeight(entry.itemId, weight);
                          }}
                        />
                      </td>
                      <td className="mono">{entry.months?.join(', ') ?? '恒常'}</td>
                      <td>
                        <button
                          className="button button--small"
                          type="button"
                          aria-label={`${entry.itemId} を外す`}
                          onClick={() => {
                            update({
                              pool: working.pool.filter((e) => e.itemId !== entry.itemId),
                            });
                          }}
                        >
                          外す
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {showPool && matchingPool.length > POOL_PAGE && (
            <p className="muted">
              先頭 {POOL_PAGE} 件を表示しています(条件で絞り込むと残りも見られます)。
            </p>
          )}
        </section>
      </fieldset>
      {editable && (
        <div className="actions">
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
          {isNew && onCancel && (
            <button className="button" type="button" onClick={onCancel}>
              取り消し
            </button>
          )}
        </div>
      )}

      <section className="card" aria-label="模擬抽選">
        <h3>模擬抽選({SIM_DRAWS.toLocaleString()}回。ブラウザ内だけで回し、何も保存しません)</h3>
        <div className="toolbar">
          <select
            className="select select--inline"
            aria-label="抽選の回し方"
            value={simMode}
            onChange={(event) => {
              setSimMode(event.target.value as SimMode);
            }}
          >
            <option value="single">単発 × {SIM_DRAWS}</option>
            <option value="multi">
              {working.guarantee.count}連 ×{' '}
              {Math.floor(SIM_DRAWS / Math.max(1, working.guarantee.count))}
              (保証あり)
            </option>
          </select>
          <button className="button button--small" type="button" onClick={runSim}>
            模擬抽選する
          </button>
        </div>
        {simError !== null && (
          <div className="notice notice--error" role="alert">
            {simError}
          </div>
        )}
        {sim !== null && (
          <>
            <table className="table table--compact" aria-label="レアリティ別の結果">
              <thead>
                <tr>
                  <th>レア</th>
                  <th>回数</th>
                  <th>割合</th>
                  <th>設定の排出率</th>
                </tr>
              </thead>
              <tbody>
                {RARITIES.map((rarity) => (
                  <tr key={rarity}>
                    <td>{rarity}</td>
                    <td>{sim.byRarity[rarity]}</td>
                    <td>{((sim.byRarity[rarity] / sim.draws) * 100).toFixed(1)}%</td>
                    <td>{((working.rarityRates[rarity] ?? 0) * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted">
              出たアイテム {sim.byItem.length} 種 / 1回も出なかったもの {sim.neverDrawn} 種
            </p>
            <div className="scroll-box">
              <table className="table table--compact" aria-label="アイテム別の結果">
                <thead>
                  <tr>
                    <th>アイテム</th>
                    <th>レア</th>
                    <th>回数</th>
                  </tr>
                </thead>
                <tbody>
                  {sim.byItem.map((row) => (
                    <tr key={row.itemId}>
                      <td>
                        <span className="mono">{row.itemId}</span>{' '}
                        <span className="muted">{itemsById.get(row.itemId)?.name.ja ?? ''}</span>
                      </td>
                      <td>{row.rarity}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </form>
  );
}
